import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, StatusBadge } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { razao_social: "", nome_fantasia: "", cnpj_cpf: "", segmento: "", grupo_economico: "", cidade: "", estado: "", telefone: "", whatsapp: "", email: "", responsavel: "", cargo: "", origem: "", status: "ativo", potencial: "medio", representadas: [], observacoes: "" };

export default function Clientes() {
  const [list, setList] = useState([]);
  const [reps, setReps] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = status !== "all" ? { status } : {};
    const { data } = await api.get("/clientes", { params });
    setList(data);
  };
  useEffect(() => { load(); }, [status]);
  useEffect(() => { api.get("/representadas").then((r) => setReps(r.data)); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/clientes/${editing}`, form);
      else await api.post("/clientes", form);
      toast.success("Salvo!"); setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error("Erro"); }
  };
  const edit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Excluir?")) return; await api.delete(`/clientes/${id}`); load(); };

  const filtered = list.filter((c) => {
    const s = q.toLowerCase();
    return !s || (c.razao_social || "").toLowerCase().includes(s) || (c.nome_fantasia || "").toLowerCase().includes(s) || (c.cnpj_cpf || "").includes(s) || (c.cidade || "").toLowerCase().includes(s);
  });

  const toggleRep = (id) => {
    const set = new Set(form.representadas || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    setForm({ ...form, representadas: Array.from(set) });
  };

  return (
    <div>
      <PageHeader title="Clientes" subtitle="CRM completo de varejo, distribuidores e redes" actions={
        <Button data-testid="btn-new-cliente" onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-[#075BFF] hover:bg-[#087BFF]"><Plus className="w-4 h-4 mr-1" />Novo cliente</Button>
      } />
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input data-testid="inp-search-clientes" placeholder="Buscar por razão social, CNPJ, cidade..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="select-status-cliente" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="prospect">Prospect</SelectItem>
            <SelectItem value="ativo">Ativo</SelectItem>
            <SelectItem value="inativo">Inativo</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/70 border-b border-slate-200 dark:border-slate-800">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Cliente</th>
              <th className="text-left px-4 py-3 font-semibold">CNPJ/CPF</th>
              <th className="text-left px-4 py-3 font-semibold">Cidade/UF</th>
              <th className="text-left px-4 py-3 font-semibold">Segmento</th>
              <th className="text-left px-4 py-3 font-semibold">Representadas</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50">
                <td className="px-4 py-3">
                  <div className="font-semibold">{c.nome_fantasia || c.razao_social}</div>
                  <div className="text-xs text-slate-500">{c.razao_social}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{c.cnpj_cpf}</td>
                <td className="px-4 py-3">{c.cidade}/{c.estado}</td>
                <td className="px-4 py-3">{c.segmento}</td>
                <td className="px-4 py-3">{(c.representadas || []).length}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => edit(c)} data-testid={`btn-edit-cliente-${c.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500 text-sm">Nenhum cliente encontrado.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} cliente</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["razao_social", "Razão Social"], ["nome_fantasia", "Nome Fantasia"], ["cnpj_cpf", "CNPJ/CPF"], ["segmento", "Segmento"], ["grupo_economico", "Grupo econômico"], ["cidade", "Cidade"], ["estado", "UF"], ["telefone", "Telefone"], ["whatsapp", "WhatsApp"], ["email", "E-mail"], ["responsavel", "Responsável compra"], ["cargo", "Cargo"], ["origem", "Origem"]].map(([k, l]) => (
              <div key={k} className="space-y-1"><Label>{l}</Label><Input data-testid={`cli-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
            ))}
            <div className="space-y-1"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="prospect">Prospect</SelectItem><SelectItem value="ativo">Ativo</SelectItem><SelectItem value="inativo">Inativo</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Potencial</Label>
              <Select value={form.potencial} onValueChange={(v) => setForm({ ...form, potencial: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="baixo">Baixo</SelectItem><SelectItem value="medio">Médio</SelectItem><SelectItem value="alto">Alto</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 space-y-1">
              <Label>Representadas vinculadas</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                {reps.map((r) => (
                  <label key={r.id} className={`flex items-center gap-2 p-2 rounded border text-xs cursor-pointer ${(form.representadas || []).includes(r.id) ? "bg-blue-500/10 border-blue-500/40" : "border-slate-200 dark:border-slate-800"}`}>
                    <input type="checkbox" data-testid={`chk-rep-${r.id}`} checked={(form.representadas || []).includes(r.id)} onChange={() => toggleRep(r.id)} />
                    <span className="truncate">{r.nome_fantasia || r.razao_social}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2 space-y-1"><Label>Observações</Label><Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button data-testid="btn-save-cliente" onClick={save} className="bg-[#075BFF] hover:bg-[#087BFF]">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
